// src/admin/screens/login/ResetPassword.tsx
//
// Handles the redirect target for Supabase password-recovery emails.
//
// Two shapes of link can land here depending on how the email was sent:
//
//   1. Dashboard "Send password recovery" (legacy implicit flow) lands with
//      tokens in the URL hash:
//        /reset-password#access_token=…&refresh_token=…&type=recovery
//
//   2. Client-side resetPasswordForEmail (PKCE flow) lands with a code query
//      param:
//        /reset-password?code=<pkce_code>
//
// Our client sets detectSessionInUrl: false (PKCE), so we must consume both
// shapes explicitly. After a session is established we show a "set new
// password" form; on success we sign out and bounce to /login so the user
// re-enters with the new password and starts the first-login TOTP flow clean.
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@admin/lib/supabase';
import styles from './Login.module.css';

const schema = z
  .object({
    password: z.string().min(12, 'At least 12 characters'),
    confirm: z.string(),
  })
  .refine((v) => v.password === v.confirm, {
    path: ['confirm'],
    message: 'Passwords do not match',
  });
type FormValues = z.infer<typeof schema>;

type InitState = 'loading' | 'ready' | { error: string };

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [state, setState] = useState<InitState>('loading');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  useEffect(() => {
    void (async () => {
      // PKCE flow: ?code=<pkce_code>
      const code = params.get('code');
      if (code) {
        const { error } = await supabase.auth.exchangeCodeForSession(code);
        if (error) {
          setState({ error: `Recovery link invalid or expired: ${error.message}` });
          return;
        }
        setState('ready');
        return;
      }

      // Legacy implicit flow: tokens in hash. Hash shape is
      // #access_token=…&refresh_token=…&type=recovery&expires_in=…
      // On failure Supabase instead sets error fields:
      // #error=access_denied&error_code=otp_expired&error_description=…
      const hash = window.location.hash.startsWith('#')
        ? window.location.hash.slice(1)
        : window.location.hash;
      if (hash) {
        const hp = new URLSearchParams(hash);
        const access_token = hp.get('access_token');
        const refresh_token = hp.get('refresh_token');
        const type = hp.get('type');
        const errCode = hp.get('error_code');
        const errDesc = hp.get('error_description');

        if (access_token && refresh_token && type === 'recovery') {
          const { error } = await supabase.auth.setSession({ access_token, refresh_token });
          if (error) {
            setState({ error: `Recovery link invalid or expired: ${error.message}` });
            return;
          }
          // Clear the sensitive tokens from the URL bar
          window.history.replaceState({}, '', '/reset-password');
          setState('ready');
          return;
        }

        if (errCode) {
          // Common cases: otp_expired (one-time link aged out or was already
          // clicked), access_denied (link consumed/revoked).
          const msg = errDesc
            ? decodeURIComponent(errDesc.replace(/\+/g, ' '))
            : errCode;
          setState({
            error: `${msg}. Recovery links are one-time use and expire after about an hour — request a new one from the sign-in screen.`,
          });
          return;
        }
      }

      // No code, no recovery hash. Might be a stale session from a prior
      // sign-in — allow password change if a session already exists.
      const { data } = await supabase.auth.getSession();
      if (data.session) {
        setState('ready');
        return;
      }
      setState({ error: 'No recovery link detected. Open the reset email on this device and click the link again.' });
    })();
  }, [params]);

  const onSubmit = async ({ password }: FormValues) => {
    setSubmitError(null);
    const { error } = await supabase.auth.updateUser({ password });
    if (error) {
      setSubmitError(error.message);
      return;
    }
    // Recovery session is aal1 and scoped to this flow. Sign out so the
    // next visit goes through the full sign-in + TOTP path.
    await supabase.auth.signOut();
    navigate('/login?reset=ok', { replace: true });
  };

  if (state === 'loading') {
    return (
      <div className={styles.page}>
        <div className={styles.card}>Verifying recovery link…</div>
      </div>
    );
  }

  if (typeof state === 'object' && 'error' in state) {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <h1>Reset password</h1>
          <div className={styles.error}>{state.error}</div>
          <button
            type="button"
            className={styles.submit}
            onClick={() => navigate('/login', { replace: true })}
          >
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <form className={styles.card} onSubmit={handleSubmit(onSubmit)} noValidate>
        <h1>Set a new password</h1>
        <p className="small">Choose a strong password — at least 12 characters.</p>
        <label>
          New password
          <input type="password" autoFocus autoComplete="new-password" {...register('password')} />
          {errors.password && <span className={styles.fieldError}>{errors.password.message}</span>}
        </label>
        <label>
          Confirm password
          <input type="password" autoComplete="new-password" {...register('confirm')} />
          {errors.confirm && <span className={styles.fieldError}>{errors.confirm.message}</span>}
        </label>
        {submitError && <div className={styles.error}>{submitError}</div>}
        <button type="submit" className={styles.submit} disabled={isSubmitting}>
          {isSubmitting ? 'Saving…' : 'Save and sign in'}
        </button>
      </form>
    </div>
  );
}
